import { useNode } from "@craftjs/core";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import React, { useState } from "react";
import { useDrag, useDrop } from "react-dnd";
import { useDispatch, useSelector } from "react-redux";
import { colors } from "../../../../utils";
import { ASSETS } from "../../../../utils/config";
import { getRandomId } from "../../../../utils/helpers";
import { toggleSegmentSettings } from "../../../../redux/actions/segmentSettings";
import { updateSocialMediaLinksStyles } from "../../../../redux/reducers/PrevStylesCopy/CodeblockDefaultStyles";
import Button from "../../../Button";
import Dropdown from "../../../Dropdown";
import Input from "../../../Input";
import BlockAlignment from "../../../SegmentBuilder/Shared/BlockAlignment";
import Tabs from "../../../Tabs";
import TextView from "../../../TextView";
import MarginBox from "../../Shared/MarginBox";
import SettingsSlider from "../../Shared/SettingsSlider";
import ColorPopup from "../../Shared/ColorPopup";
import {
  IconsArray,
  platformPatterns,
  SocialIconsArray,
  SocialLinks,
  SocialLinksArray,
} from "../../../../utils/socialHelpers";
import * as Styled from "./styles";
import UndoRedoComponent from "../../../../pages/SegmentBuilder/UndoRedo";
import { Tooltip } from "@mui/material";

const columnsArray = [
  { value: 1, label: "1" },
  { value: 2, label: "2" },
  { value: 3, label: "3" },
  { value: 4, label: "4" },
  { value: 5, label: "5" },
  { value: 6, label: "6" },
  { value: 7, label: "7" },
  { value: 8, label: "8" },
];

const ITEM_TYPE = "SOCIAL_ICON";

const SocialLinksSettings = () => {
  const {
    actions: { setProp },
    socialLinks,
    alignment,
    iconsPerRow,
    margin,
    isApplyMarginOnAllSides,
    allSideMargin,
  } = useNode((node) => ({
    socialLinks: node.data.props.socialLinks,
    alignment: node.data.props.alignment,
    iconsPerRow: node.data.props.iconsPerRow,
    isApplyMarginOnAllSides: node.data.props.isApplyMarginOnAllSides,
    margin: node.data.props.margin,
    allSideMargin: node.data.props.allSideMargin,
  }));

  const dispatch = useDispatch();
  const { socialMediaLinks: socialMediaLinksProps } = useSelector((state) => state.codeblockStyles);

  const mainTabs = ["Social Platforms Selection", "Layout"];
  const [selectedTab, setSelectedTab] = useState(mainTabs[0]);
  const [selectedIcon, setSelectedIcon] = useState(() => {
    return socialLinks && socialLinks.length > 0 ? socialLinks[0].id : null;
  });

  const validateLink = (link, platform) => {
    if (!link) return false;

    const pattern = platformPatterns[platform];
    return pattern ? pattern.test(link) : true;
  };

  const handleAdjustment = (side, operation) => {
    const newValue =
      operation === "increase" ? margin[side] + 1 : margin[side] - 1;
    if (newValue >= 0) handleChange("margin", newValue, side);
  };

  const handleChange = (type, value, side) => {
    dispatch(toggleSegmentSettings(false));
    
    setProp((props) => {
      // Clone the props to avoid mutating Proxies
      const newProps = {
        ...props,
        socialLinks: [...props.socialLinks], // deep clone the socialLinks array
        margin: props.margin ? { ...props.margin } : undefined
      };
        
      const updatedLinks = [...props.socialLinks];
      const selectedLinkIndex = updatedLinks.findIndex(
        (link) => link.id === selectedIcon
      );

      if (selectedLinkIndex === -1 && type !== "alignment" && 
          type !== "iconsPerRow" && type !== "margin" && 
          type !== "allSideMargin" && type !== "marginCheckbox") return;

      switch (type) {
        case "alignment":
          props.alignment = value;
          newProps.alignment = value;
          break;
        case "iconsPerRow":
          props.iconsPerRow = value;
          newProps.iconsPerRow = value;
          break;
        case "margin":
          if (value >= 0 && value <= 100) {
            props.margin[side] = value;
            newProps.margin[side] = value;
          }
          break;
        case "allSideMargin":
          props.allSideMargin = value;
          newProps.allSideMargin = value;
          break;
        case "marginCheckbox":
          props.isApplyMarginOnAllSides = !props.isApplyMarginOnAllSides;
          newProps.isApplyMarginOnAllSides = !props.isApplyMarginOnAllSides;
          break;
        case "socialLinks":
          updatedLinks[selectedLinkIndex].platform = value;
          updatedLinks[selectedLinkIndex].icon =
            SocialIconsArray[value]?.[0] ||
            SocialIconsArray[SocialLinks.web][0];
          updatedLinks[selectedLinkIndex].isLinkValid = validateLink(
            updatedLinks[selectedLinkIndex].link,
            value
          );
          props.socialLinks = updatedLinks;
          newProps.socialLinks = [...updatedLinks];
          break;
        case "icon":
          updatedLinks[selectedLinkIndex].icon = value;
          props.socialLinks = updatedLinks;
          newProps.socialLinks = [...updatedLinks];
          break;
        case "link":
          updatedLinks[selectedLinkIndex].link = value;
          updatedLinks[selectedLinkIndex].isLinkValid = validateLink(
            value,
            updatedLinks[selectedLinkIndex].platform
          );
          props.socialLinks = updatedLinks;
          newProps.socialLinks = [...updatedLinks];
          break;
        default:
          break;
      }
        
      // Dispatch the updated props to redux
      dispatch(updateSocialMediaLinksStyles(newProps));
    });
  };

  const onAddSocialLink = () => {
    dispatch(toggleSegmentSettings(false));
    
    setProp((props) => {
      const newId = getRandomId();
      const newLink = {
        id: newId,
        platform: SocialLinks.web,
        icon: SocialIconsArray[SocialLinks.web][0],
        link: "",
        isLinkValid: false,
      };
      
      const updatedLinks = [...props.socialLinks, newLink];
      props.socialLinks = updatedLinks;
      
      // Also update in Redux
      const newProps = {
        ...props,
        socialLinks: updatedLinks
      };
      
      dispatch(updateSocialMediaLinksStyles(newProps));
      setSelectedIcon(newId);
    });
  };

  const onDeleteSocialLink = () => {
    if (selectedIcon) {
      setProp((props) => {
        const updatedLinks = props.socialLinks.filter(
          (link) => link.id !== selectedIcon
        );

        props.socialLinks = updatedLinks;

        if (updatedLinks.length > 0) {
          setSelectedIcon(updatedLinks[0].id);
        } else {
          setSelectedIcon(null);
        }
        
        // Also update in Redux
        const newProps = {
          ...props,
          socialLinks: updatedLinks
        };
        
        dispatch(updateSocialMediaLinksStyles(newProps));
      });
    }
  };

  const moveIcon = (dragIndex, hoverIndex) => {
    setProp((props) => {
      const updatedLinks = [...props.socialLinks];
      const [draggedIcon] = updatedLinks.splice(dragIndex, 1);
      updatedLinks.splice(hoverIndex, 0, draggedIcon);
      props.socialLinks = updatedLinks;
      
      // Also update in Redux
      const newProps = {
        ...props,
        socialLinks: updatedLinks
      };
      
      dispatch(updateSocialMediaLinksStyles(newProps));
    });
  };

  const DraggableIcon = ({ link, index }) => {
    const ref = React.useRef(null);

    const [, drop] = useDrop({
      accept: ITEM_TYPE,
      hover(item) {
        if (!ref.current) return;
        const dragIndex = item.index;
        const hoverIndex = index;
        if (dragIndex === hoverIndex) return;
        moveIcon(dragIndex, hoverIndex);
        item.index = hoverIndex;
      },
    });

    const [{ isDragging }, drag] = useDrag({
      type: ITEM_TYPE,
      item: { index },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    });

    drag(drop(ref));

    const IconComponent = IconsArray.find(
      (icon) => icon.id === link.icon
    )?.icon;

    return (
      <Styled.Icon
        ref={ref}
        style={{ opacity: isDragging ? 0.5 : 1 }}
        onClick={() => setSelectedIcon(link.id)}
        isActive={link.id === selectedIcon}
      >
        <IconComponent />
      </Styled.Icon>
    );
  };

  const renderIconSelection = () => {
    const isSocialLinksEmpty = !socialLinks || socialLinks.length === 0;

    if (isSocialLinksEmpty) {
      return (
        <Styled.NoLinksWrapper>
          <img src={ASSETS.segmentEditor.SocialLinks} alt="No Links" />
          <TextView>Add Social Media Links</TextView>
          <Button
            style={{ backgroundColor: colors.purple, width: 70 }}
            onClick={onAddSocialLink}
          >
            Add
          </Button>
        </Styled.NoLinksWrapper>
      );
    } else {
      const availableSocialLinks = SocialLinksArray.filter(
        (socialPlatform) =>
          !socialLinks.some(
            (link) => link?.platform === socialPlatform.value
          ) ||
          socialPlatform.value ===
            socialLinks.find((link) => link.id === selectedIcon)?.platform
      );

      const selectedLink = socialLinks.find((link) => link.id === selectedIcon);
      const linkIcon =
        !selectedLink?.link || !selectedLink?.isLinkValid
          ? ASSETS.linkError
          : ASSETS.linkSuccess;

      const tooltip =
        !selectedLink?.link || !selectedLink?.isLinkValid
          ? "Invalid URL - please copy the link directly from the website."
          : "URL Validation Successful!";

      return (
        <>
          <TextView variant="subtitle">Social Platforms</TextView>
          <Styled.Row
            style={{
              margin: "10px 0px",
              justifyContent: "space-between",
            }}
          >
            <Styled.IconsWrapper>
              {socialLinks.map((link, index) => (
                <DraggableIcon
                  key={`selected-icon-${index}`}
                  link={link}
                  index={index}
                />
              ))}
            </Styled.IconsWrapper>

            <Styled.IconActions>
              <Styled.IconView
                onClick={onAddSocialLink}
                style={{ backgroundColor: colors.transparentPurple }}
              >
                <AddIcon style={{ color: colors.purple }} />
              </Styled.IconView>
              <Styled.IconView
                onClick={onDeleteSocialLink}
                style={{ backgroundColor: colors.background }}
              >
                <DeleteIcon style={{ color: colors.darkGrey }} />
              </Styled.IconView>
            </Styled.IconActions>
          </Styled.Row>

          {selectedIcon && (
            <>
              <Dropdown
                label={"Social Link"}
                subLabel={
                  "Select a social media platform below to link in the segment!"
                }
                labelVariant={"subtitle"}
                variant="outlined"
                options={availableSocialLinks}
                placeholder="Select social platform"
                value={
                  socialLinks.find((link) => link.id === selectedIcon)?.platform
                }
                defaultValue={
                  socialLinks.find((link) => link.id === selectedIcon)?.platform
                }
                onChange={(e) => handleChange("socialLinks", e.target.value)}
                style={{ marginBottom: 20 }}
              />
              <Styled.Row>
                <Input
                  containerStyle={{ width: "100%" }}
                  label={"URL"}
                  variant={"secondary"}
                  value={
                    socialLinks.find((link) => link.id === selectedIcon)
                      ?.link || ""
                  }
                  onChange={(e) => handleChange("link", e.target.value)}
                />

                <Tooltip title={tooltip} placement="top">
                  <img
                    src={linkIcon}
                    alt={linkIcon === ASSETS.linkError ? "cross" : "check"}
                    style={{ height: "22px", width: "22px",marginTop:30 }}
                  />
                </Tooltip>
              </Styled.Row>

              {socialLinks.find((link) => link.id === selectedIcon)
                ?.platform && (
                <>
                  <TextView variant="subtitle">Icon</TextView>
                  <Styled.IconsWrapper>
                    {SocialIconsArray[
                      socialLinks.find((link) => link.id === selectedIcon)
                        ?.platform
                    ]?.map((iconId, index) => (
                      <Styled.Icon
                        onClick={() => handleChange("icon", iconId)}
                        key={`brand-icon-${index}`}
                        isActive={
                          socialLinks.find((link) => link.id === selectedIcon)
                            .icon === iconId
                        }
                      >
                        {React.createElement(
                          IconsArray.find((icon) => icon.id === iconId)?.icon
                        )}
                      </Styled.Icon>
                    ))}
                  </Styled.IconsWrapper>
                </>
              )}
            </>
          )}
        </>
      );
    }
  };

  const renderIconLayout = () => {
    return (
      <>
        <BlockAlignment
          style={{ marginBottom: 20 }}
          selectedAlignment={alignment}
          setSelectedAlignment={(value) => handleChange("alignment", value)}
        />
        <Dropdown
          label={"Default Icons per Row"}
          labelVariant={"subtitle"}
          variant="outlined"
          options={columnsArray}
          value={iconsPerRow}
          defaultValue={iconsPerRow}
          onChange={(e) => handleChange("iconsPerRow", e.target.value)}
          style={{ marginBottom: 20 }}
        />
        <MarginBox
          margin={margin}
          increaseMargin={(side) => handleAdjustment(side, "increase")}
          decreaseMargin={(side) => handleAdjustment(side, "decrease")}
          handleMarginChange={(side, value) =>
            handleChange("margin", value, side)
          }
          isApplyMarginOnAllSides={isApplyMarginOnAllSides}
          allSideMargin={allSideMargin}
          handleMarginCheckBox={() => handleChange("marginCheckbox")}
          handleSliderMarginChange={(value) =>
            handleChange("allSideMargin", value)
          }
        />
      </>
    );
  };

  return (
    <>
      <Tabs
        categories={mainTabs}
        selectedCategory={selectedTab}
        onCategoryChange={(_, val) => setSelectedTab(val)}
        tabStyle={{ maxWidth: "50%" }}
        disabledCategories={socialLinks.length ? [] : [mainTabs[1]]}
      />
      <div style={{ marginBottom: 20 }} />
      {selectedTab === mainTabs[0] && renderIconSelection()}
      {selectedTab === mainTabs[1] && renderIconLayout()}
    </>
  );
};

export default SocialLinksSettings;
